<template>
  <div class="carrier-portal-actions">
    <template v-for="(action, i) in availableActions" :key="action.key ?? i">
      <span v-if="action.separator" class="separator">|</span>
      <RygenButton
        v-else
        mode="primary"
        :disabled="loading"
        :icon="action.icon"
        :label="action.label"
        :loading="isPrimaryAction(action) && loading"
        :severity="action.severity"
        @click="action.command?.({ originalEvent: $event, item: action })"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  ActionCommand,
  RygenMenuItem,
  ServiceIncidentType,
  Severity,
} from '@rygen/rygen-ui/model';

import { useShipmentStops } from '@/composables';
import {
  CarrierPortalAction,
  CarrierShipmentSummary,
  Shipment,
  ShipmentAction,
  ShipmentEventSeverity,
  ShipmentStatus,
  TenderResponseCode,
} from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';

const props = defineProps({
  shipmentSummary: {
    type: CarrierShipmentSummary,
    default: () => new CarrierShipmentSummary(),
  },
  serviceIncidentTypes: {
    type: Array as PropType<ServiceIncidentType[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { shipmentSummary, serviceIncidentTypes, loading } = toRefs(props);
const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<CarrierPortalAction>): void;
}>();

const shipment = toRef(() => shipmentSummary.value?.shipment ?? new Shipment());
const shipmentEvents = toRef(() => shipmentSummary.value?.shipmentEvents ?? []);

const { t } = useI18n();
const { firstUnresolvedStop } = useShipmentStops(shipment, shipmentEvents);

const isAccepted = computed(
  () =>
    shipmentSummary.value.tenderRequest?.tenderResponse?.responseCode ===
    TenderResponseCode.RESERVATION_ACCEPTED,
);

const isRejected = computed(
  () =>
    shipmentSummary.value.tenderRequest?.tenderResponse?.responseCode ===
    TenderResponseCode.RESERVATION_DECLINED,
);

const isErrored = computed(
  () =>
    shipmentSummary.value.tenderResponseMessage &&
    shipmentSummary.value.tenderResponseMessage !== 'SUCCESS',
);

// An unauthenticated access token is tied to permission on the backend, but the
// frontend just has the uuid so it can't read the permissions.
// TODO: Use Spring Security for unauthenticated access tokens and enable authorization checks below
const actions: Ref<{
  [shipmentAction in CarrierPortalAction]: RygenMenuItem;
}> = computed(() => ({
  [CarrierPortalAction.ATTACH_DOCUMENT]: {
    key: CarrierPortalAction.ATTACH_DOCUMENT,
    label: t('enumMap.CarrierPortalAction.ATTACH_DOCUMENT'),
    icon: 'pi pi-upload',
    command: () =>
      emit('command', { action: CarrierPortalAction.ATTACH_DOCUMENT }),
    // requiredAuthorization: ['document', 'create'],
    visible: isAccepted.value,
  },
  [CarrierPortalAction.TENDER_CONFIRM]: {
    key: CarrierPortalAction.TENDER_CONFIRM,
    label: t('enumMap.CarrierPortalAction.TENDER_CONFIRM'),
    icon: 'pi pi-check',
    command: () =>
      emit('command', { action: CarrierPortalAction.TENDER_CONFIRM }),
    // requiredAuthorization: ['shipment', 'update'],
    visible: !isErrored.value && isRejected.value,
  },
  [CarrierPortalAction.TENDER_REJECT]: {
    key: CarrierPortalAction.TENDER_REJECT,
    label: t('enumMap.CarrierPortalAction.TENDER_REJECT'),
    icon: 'pi pi-minus-circle',
    severity: Severity.DANGER,
    command: () =>
      emit('command', { action: CarrierPortalAction.TENDER_REJECT }),
    // requiredAuthorization: ['shipment', 'update'],
    visible: !isErrored.value && isAccepted.value,
  },
  [CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL]: {
    key: CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL,
    label: t('enumMap.CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL'),
    icon: 'pi pi-calendar-plus',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL,
        relatedStop: firstUnresolvedStop.value,
      }),
    // requiredAuthorization: ['shipment', 'update'],
    visible: !firstUnresolvedStop.value?.actualArrival && isAccepted.value,
  },
  [CarrierPortalAction.PICK_UP_ARRIVE]: {
    key: CarrierPortalAction.PICK_UP_ARRIVE,
    label: t('enumMap.CarrierPortalAction.PICK_UP_ARRIVE'),
    icon: 'pi pi-map-marker',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.PICK_UP_ARRIVE,
        relatedStop: firstUnresolvedStop.value,
        actualDate: DateTime.now(),
        severity: ShipmentEventSeverity.NORMAL,
      }),
    // requiredAuthorization: ['shipmentEvent', 'update'],
    visible:
      firstUnresolvedStop.value?.isPickup &&
      !firstUnresolvedStop.value.actualArrival &&
      isAccepted.value,
  },
  [CarrierPortalAction.PICK_UP_LOAD]: {
    key: CarrierPortalAction.PICK_UP_LOAD,
    label: t('enumMap.CarrierPortalAction.PICK_UP_LOAD'),
    icon: 'pi pi-map-marker',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.PICK_UP_LOAD,
        relatedStop: firstUnresolvedStop.value,
        actualDate: DateTime.now(),
        severity: ShipmentEventSeverity.NORMAL,
      }),
    // requiredAuthorization: ['shipmentEvent', 'create'],
    visible:
      firstUnresolvedStop.value?.isPickup &&
      firstUnresolvedStop.value.isInProgress &&
      isAccepted.value,
  },
  [CarrierPortalAction.PICK_UP_DEPART]: {
    key: CarrierPortalAction.PICK_UP_DEPART,
    label: t('enumMap.CarrierPortalAction.PICK_UP_DEPART'),
    icon: 'pi pi-map-marker',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.PICK_UP_DEPART,
        relatedStop: firstUnresolvedStop.value,
        actualDate: DateTime.now(),
        severity: ShipmentEventSeverity.NORMAL,
      }),
    // requiredAuthorization: ['shipmentEvent', 'update'],
    visible:
      firstUnresolvedStop.value?.isPickup &&
      firstUnresolvedStop.value.isInProgress &&
      isAccepted.value,
  },
  [CarrierPortalAction.DROPOFF_ARRIVE]: {
    key: CarrierPortalAction.DROPOFF_ARRIVE,
    label: t('enumMap.CarrierPortalAction.DROPOFF_ARRIVE'),
    icon: 'pi pi-map-marker',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.DROPOFF_ARRIVE,
        relatedStop: firstUnresolvedStop.value,
        actualDate: DateTime.now(),
        severity: ShipmentEventSeverity.NORMAL,
      }),
    // requiredAuthorization: ['shipmentEvent', 'update'],
    visible:
      firstUnresolvedStop.value?.isDropOff &&
      !firstUnresolvedStop.value.actualArrival &&
      isAccepted.value,
  },
  [CarrierPortalAction.DROPOFF_UNLOAD]: {
    key: CarrierPortalAction.DROPOFF_UNLOAD,
    label: t('enumMap.CarrierPortalAction.DROPOFF_UNLOAD'),
    icon: 'pi pi-map-marker',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.DROPOFF_UNLOAD,
        relatedStop: firstUnresolvedStop.value,
        actualDate: DateTime.now(),
        severity: ShipmentEventSeverity.NORMAL,
      }),
    // requiredAuthorization: ['shipmentEvent', 'create'],
    visible:
      firstUnresolvedStop.value?.isDropOff &&
      firstUnresolvedStop.value.isInProgress &&
      isAccepted.value,
  },
  [CarrierPortalAction.DROPOFF_DEPART]: {
    key: CarrierPortalAction.DROPOFF_DEPART,
    label: t('enumMap.CarrierPortalAction.DROPOFF_DEPART'),
    icon: 'pi pi-map-marker',
    command: () =>
      emit('command', {
        action: CarrierPortalAction.DROPOFF_DEPART,
        relatedStop: firstUnresolvedStop.value,
        actualDate: DateTime.now(),
        severity: ShipmentEventSeverity.NORMAL,
      }),
    // requiredAuthorization: ['shipmentEvent', 'update'],
    visible:
      firstUnresolvedStop.value?.isDropOff &&
      firstUnresolvedStop.value.isInProgress &&
      isAccepted.value,
  },
  [CarrierPortalAction.IN_TRANSIT]: {
    key: CarrierPortalAction.IN_TRANSIT,
    label: t('enumMap.CarrierPortalAction.IN_TRANSIT'),
    icon: 'pi pi-truck',
    command: () => emit('command', { action: CarrierPortalAction.IN_TRANSIT }),
    // requiredAuthorization: ['shipmentEvent', 'create'],
    visible: isAccepted.value,
  },
  [CarrierPortalAction.INCIDENT]: {
    key: CarrierPortalAction.INCIDENT,
    label: t('enumMap.CarrierPortalAction.INCIDENT'),
    icon: 'pi pi-exclamation-triangle',
    severity: Severity.WARNING,
    command: () => emit('command', { action: CarrierPortalAction.INCIDENT }),
    // requiredAuthorization: ['shipmentEvent', 'create'],
    visible: !!serviceIncidentTypes.value.length && isAccepted.value,
  },
}));

const primaryActionsByStatus: Ref<{
  [shipmentStatus in ShipmentStatus]: RygenMenuItem[];
}> = computed(() => ({
  [ShipmentStatus.PLANNING]: [],
  [ShipmentStatus.CARRIER_ASSIGNED]: [],
  [ShipmentStatus.TENDERED]: [
    actions.value[CarrierPortalAction.TENDER_CONFIRM],
    actions.value[CarrierPortalAction.TENDER_REJECT],
    { separator: true },
    actions.value[CarrierPortalAction.INCIDENT],
    { separator: true },
    actions.value[CarrierPortalAction.ATTACH_DOCUMENT],
  ],
  [ShipmentStatus.TENDER_REJECTED]: [
    actions.value[CarrierPortalAction.TENDER_CONFIRM],
    { separator: true },
    actions.value[CarrierPortalAction.INCIDENT],
    { separator: true },
    actions.value[CarrierPortalAction.ATTACH_DOCUMENT],
  ],
  [ShipmentStatus.BOOKED]: [
    actions.value[ShipmentAction.TENDER_REJECT],
    { separator: true },
    actions.value[CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL],
    actions.value[CarrierPortalAction.PICK_UP_ARRIVE],
    actions.value[CarrierPortalAction.PICK_UP_LOAD],
    actions.value[CarrierPortalAction.PICK_UP_DEPART],
    actions.value[CarrierPortalAction.DROPOFF_ARRIVE],
    actions.value[CarrierPortalAction.DROPOFF_UNLOAD],
    actions.value[CarrierPortalAction.DROPOFF_DEPART],
    actions.value[CarrierPortalAction.IN_TRANSIT],
    actions.value[CarrierPortalAction.INCIDENT],
    { separator: true },
    actions.value[CarrierPortalAction.ATTACH_DOCUMENT],
  ],
  [ShipmentStatus.IN_TRANSIT]: [
    actions.value[CarrierPortalAction.UPDATE_EXPECTED_ARRIVAL],
    actions.value[CarrierPortalAction.PICK_UP_ARRIVE],
    actions.value[CarrierPortalAction.PICK_UP_LOAD],
    actions.value[CarrierPortalAction.PICK_UP_DEPART],
    actions.value[CarrierPortalAction.DROPOFF_ARRIVE],
    actions.value[CarrierPortalAction.DROPOFF_UNLOAD],
    actions.value[CarrierPortalAction.DROPOFF_DEPART],
    actions.value[CarrierPortalAction.IN_TRANSIT],
    actions.value[CarrierPortalAction.INCIDENT],
    { separator: true },
    actions.value[CarrierPortalAction.ATTACH_DOCUMENT],
  ],
  [ShipmentStatus.DELIVERED]: [
    actions.value[CarrierPortalAction.IN_TRANSIT],
    actions.value[CarrierPortalAction.INCIDENT],
    { separator: true },
    actions.value[CarrierPortalAction.ATTACH_DOCUMENT],
  ],
  [ShipmentStatus.CANCELED]: [],
  [ShipmentStatus.COMPLETE]: [
    actions.value[CarrierPortalAction.ATTACH_DOCUMENT],
  ],
}));

const availableActions = computed(() => {
  if (!shipment.value.status) {
    return [];
  }

  const availableActions =
    primaryActionsByStatus.value[shipment.value.status] ?? [];
  return availableActions
    .filter((action) => {
      const isVisible =
        typeof action.visible === 'function'
          ? action.visible()
          : action.visible !== false;

      if (action.requiredAuthorization) {
        const [resource, actionOnResource] = action.requiredAuthorization;
        return (
          isVisible && authorization.hasAuthority(resource, actionOnResource)
        );
      }

      // if authorization is null then no authorization is required
      return isVisible;
    })
    .filter((action, index, self) => {
      // This filter has to run after the first so that all the actions that
      // shouldn't be shown are removed. Now do a second pass to remove
      // separators that don't separate anything anymore.

      const isFirstItem = index === 0;
      const isLastItem = index === self.length - 1;
      return (
        // The first item shouldn't be a separator
        !(action.separator && isFirstItem) &&
        // The last item shouldn't be a separator
        !(action.separator && isLastItem) &&
        // A separator shouldn't be followed by a separator
        !(action.separator && self[index + 1].separator)
      );
    });
});

const primaryAvailableAction: Ref<RygenMenuItem | undefined> = computed(() => {
  return availableActions.value.find((action: RygenMenuItem) => {
    return !action.separator;
  });
});

const isPrimaryAction = (action: RygenMenuItem) => {
  return action.key === primaryAvailableAction.value?.key;
};
</script>

<style>
.carrier-portal-actions {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  flex-flow: row wrap;
  flex: 0 1 auto;

  .separator {
    font-size: 1.2rem;
    z-index: 9;
  }
}
</style>
